import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Box, Stack } from "@mui/system";

import { Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { useUpdateCompaniesMutation } from "@/pages/Private/redux/companies/companies.api";

import { YearlyOrMonthly } from "@/enum/company.enum";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";

import { InputField } from "@/components/InputField/InputField";
import { Checkbox } from "@/components/Checkbox/Checkbox";

import { useGetProductsQuery } from "@/pages/Private/redux/subscription/subscription.api";

import { Companies, UpdateCompanies, UpdateCompaniesSchema } from "../schema/companies";

interface updateCompaniesModalProps {
	companies: Companies;
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const UpdateCompaniesModal: FunctionComponent<updateCompaniesModalProps> = ({
	companies,
	isOpen,
	setIsOpen,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companies.${key}`);
	const [update, { isLoading, error }] = useUpdateCompaniesMutation();

	const [currentFormState, setCurrentFormState] = useState<UpdateCompanies>({
		name: companies.name,
		linkedinUrl: companies.linkedinUrl || "",
		filtered: companies.filtered || 0,
		yearlyOrMonthly: companies.yearlyOrMonthly,
		demoEndsInDays: companies.demoEndsInDays || 30,
		website: companies.website || "",
		allowInstallments: companies.allowInstallments || false,
		allowBankTransfer: companies.allowBankTransfer || false,
	});

	const currentSubscription = companies.subscriptions?.[0];

	const futureSubscription = companies.subscriptions?.[1];

	const { data: subscriptions, isLoading: isSubscriptionsLoading } = useGetProductsQuery();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSaveFormState = (value: any, name: keyof UpdateCompanies) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const {
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<UpdateCompanies>({
		defaultValues: {
			name: companies.name,
			linkedinUrl: companies.linkedinUrl || "",
			filtered: companies.filtered || 0,
			yearlyOrMonthly: companies.yearlyOrMonthly,
			demoEndsInDays: companies.demoEndsInDays || 30,
			website: companies.website || "",
			allowInstallments: companies.allowInstallments || false,
			allowBankTransfer: companies.allowBankTransfer || false,
		},
		resolver: zodResolver(UpdateCompaniesSchema),
	});

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: UpdateCompanies) => {
		try {
			await update({ ...values, id: companies.id }).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.confirm")}
			title={ts("update.title")}
		>
			<Stack spacing={2}>
				<InputField
					error={!!errors.name?.message}
					handleChange={(e) => handleSaveFormState(e.target.value, "name")}
					label={ts("name")}
					name={"name"}
					placeholder={ts("name")}
					value={currentFormState.name}
				/>

				<InputField
					error={!!errors.linkedinUrl?.message}
					handleChange={(e) => {
						handleSaveFormState(e.target.value, "linkedinUrl");
					}}
					label={ts("linkedinUrl")}
					name={"linkedinUrl"}
					placeholder={ts("linkedinUrl")}
					value={currentFormState.linkedinUrl || ""}
				/>

				<InputField
					error={!!errors.website?.message}
					handleChange={(e) => handleSaveFormState(e.target.value, "website")}
					label={"Website"}
					name={"website"}
					placeholder={"Website"}
					value={currentFormState.website || ""}
				/>

				<InputField
					error={!!errors.demoEndsInDays?.message}
					handleChange={(e) => handleSaveFormState(+e.target.value, "demoEndsInDays")}
					label={"Demo ends in days"}
					name={"demoEndsInDays"}
					placeholder={"Demo ends in days"}
					value={currentFormState.demoEndsInDays || "30"}
				/>

				{currentSubscription && (
					<div className="flex flex-col mb-8">
						<div className="text-smd mb-2">Current subscription:</div>
						<div className="text-sm mb-1 !mt-1">Name: {currentSubscription?.product.name}</div>
						<div className="text-sm mb-1 !mt-1">
							Credits: {currentSubscription?.product.credits}
						</div>
						<div className="text-sm mb-1 !mt-1">
							Price: {currentSubscription?.price.price}€ /{" "}
							{currentSubscription?.price.perMonthOrYear?.toLowerCase()}
						</div>
						<div className="text-sm mb-1 !mt-1">
							Started at:{" "}
							{currentSubscription?.startedAt &&
								new Date(currentSubscription?.startedAt).toLocaleDateString("de-DE", {
									year: "numeric",
									month: "2-digit",
									day: "2-digit",
								})}
						</div>
					</div>
				)}

				{futureSubscription && (
					<>
						<div className="text-smd mb-2">Next subscription:</div>
						<div className="text-sm mb-1 !mt-1">Name: {futureSubscription?.product.name}</div>
						<div className="text-sm mb-1 !mt-1">Credits: {futureSubscription?.product.credits}</div>
						<div className="text-sm mb-1 !mt-1">
							Price: {futureSubscription?.price.price}€ /{" "}
							{futureSubscription?.price.perMonthOrYear?.toLowerCase()}
						</div>

						<div className="text-sm mb-1 !mt-1">
							Started at:{" "}
							{futureSubscription?.startedAt &&
								new Date(futureSubscription?.startedAt).toLocaleDateString("de-DE", {
									year: "numeric",
									month: "2-digit",
									day: "2-digit",
								})}
						</div>
					</>
				)}

				<Checkbox
					isChecked={currentFormState.allowInstallments || false}
					onChange={() =>
						handleSaveFormState(!currentFormState.allowInstallments, "allowInstallments")
					}
				>
					Allow Installment Payments (Semi-Annual Billing)
				</Checkbox>

				<Checkbox
					isChecked={currentFormState.allowBankTransfer || false}
					testId="allow-bank-transfer-checkbox"
					onChange={() =>
						handleSaveFormState(!currentFormState.allowBankTransfer, "allowBankTransfer")
					}
				>
					Allow bank transfer payments
				</Checkbox>
				<p className="text-xs text-text-meta -mt-1 ml-7">
					When enabled, this company can pay subscription fees and buy credits via EU bank transfer
					(manual wire to a virtual IBAN). Leave off unless the customer has explicitly requested
					it.
				</p>

				{!currentSubscription?.stripeSubscriptionId &&
					!futureSubscription?.stripeSubscriptionId && (
						<>
							<Dropdown
								data={
									subscriptions?.data.map(
										(subscription) =>
											({
												title: `${subscription.name} - ${
													subscription.prices.find(
														(price) => price.perMonthOrYear === currentFormState.yearlyOrMonthly
													)?.price
												}€ / ${subscription.prices
													.find(
														(price) => price.perMonthOrYear === currentFormState.yearlyOrMonthly
													)
													?.perMonthOrYear?.toLowerCase()}`,
												id: subscription.prices.find(
													(price) => price.perMonthOrYear === currentFormState.yearlyOrMonthly
												)?.id,
											}) as AutoCompleteItem
									) || []
								}
								disabled={isSubscriptionsLoading}
								floating={true}
								handleSelect={function (value?: AutoCompleteItem) {
									if (value?.id && value?.id !== currentSubscription?.price.id) {
										handleSaveFormState(value?.id, "subscriptionId");
									}
								}}
								label="Select another subscription"
							/>

							<Dropdown
								data={[
									{
										title: "Monthly",
										id: YearlyOrMonthly.MONTHLY,
									},
									{
										title: "Yearly",
										id: YearlyOrMonthly.YEARLY,
									},
								]}
								defaultValue={
									currentSubscription?.price.perMonthOrYear
										? {
												title:
													currentSubscription?.price.perMonthOrYear === YearlyOrMonthly.MONTHLY
														? "Monthly"
														: "Yearly",
												id: currentSubscription?.price.perMonthOrYear,
											}
										: undefined
								}
								floating={true}
								handleSelect={function (value?: AutoCompleteItem) {
									if (value?.id && value?.id !== currentFormState.yearlyOrMonthly) {
										handleSaveFormState(value?.id, "yearlyOrMonthly");
									}
								}}
								label="Type"
							/>
						</>
					)}
			</Stack>
			{getAllErrors(error, formErrors).length ? (
				<Box sx={{ mt: 2 }}>{renderErrorMessages(getAllErrors(error, formErrors))}</Box>
			) : null}
		</Modal>
	);
};
